import React from 'react';
import { Helmet } from 'react-helmet-async';
import MainLayout from '../layouts/MainLayout';
import { PhoneOutlined, ClockCircleOutlined, EnvironmentOutlined, ToolOutlined, CarOutlined, SafetyOutlined } from '@ant-design/icons';
import { Button } from 'antd';

const Help = () => {
    const services = [
        {
            icon: <CarOutlined className="text-4xl text-orange-600" />,
            title: 'Lastik Değişimi',
            description: 'Yol kenarında patlayan lastiğinizi anında değiştiriyoruz. Mobil ekibimiz 30 dakika içinde yanınızda.'
        },
        {
            icon: <ToolOutlined className="text-4xl text-orange-600" />,
            title: 'Lastik Tamiri',
            description: 'Küçük delik ve yırtıkları yerinde tamir ediyoruz. Profesyonel tamir ekipmanlarımızla güvenli çözüm.'
        },
        {
            icon: <SafetyOutlined className="text-4xl text-orange-600" />,
            title: 'Hava Basıncı Kontrolü',
            description: 'Düşük basınç nedeniyle yolda kalan aracınıza müdahale ediyoruz. Güvenli sürüş için doğru basınç ayarı.'
        }
    ];

    const emergencySteps = [
        {
            step: 1,
            title: 'Bizi Arayın',
            description: '7/24 acil yardım hattımızı arayın ve konumunuzu bildirin',
            icon: '📞'
        },
        {
            step: 2,
            title: 'Lokasyon Bildirin',
            description: 'Bulunduğunuz yeri ve araç bilgilerinizi paylaşın',
            icon: '📍'
        },
        {
            step: 3,
            title: 'Ekibimizi Bekleyin',
            description: 'Mobil ekibimiz 30 dakika içinde yanınızda olacak',
            icon: '⏱️'
        },
        {
            step: 4,
            title: 'Sorun Çözülsün',
            description: 'Uzman ekibimiz lastik sorununu yerinde çözecek',
            icon: '✅'
        }
    ];

    return (
        <MainLayout>
            <Helmet>
                <title>7/24 Yol Yardım - Sakarya Lastik | Acil Lastik Servisi</title>
                <meta name="description" content="Sakarya Lastik 7/24 yol yardım hizmeti. Patlak lastik, lastik tamiri, acil servis. Her an yanınızdayız!" />
                <meta name="keywords" content="yol yardım, acil lastik servisi, patlak lastik, 7/24 yardım, sakarya yol yardım" />
            </Helmet>

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-16">
                <div className="container mx-auto px-4">
                    <div className="flex items-center mb-4">
                        <PhoneOutlined className="text-5xl mr-4" />
                        <div>
                            <h1 className="text-4xl md:text-5xl font-bold">7/24 Yol Yardım</h1>
                            <p className="text-xl text-orange-100 mt-2">
                                Acil durumlarınızda her an yanınızdayız!
                            </p>
                        </div>
                    </div>
                    <div className="bg-white text-gray-800 rounded-lg p-6 mt-8 max-w-md">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Acil Yardım Hattı</p>
                                <p className="text-3xl font-bold text-orange-600">
                                    <a href="tel:+905441205454">0544 120 54 54</a>
                                </p>
                            </div>
                            <ClockCircleOutlined className="text-5xl text-green-500" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Services */}
            <div className="container mx-auto px-4 py-12">
                <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
                    Yol Yardım Hizmetlerimiz
                </h2>
                <div className="grid md:grid-cols-3 gap-8 mb-16">
                    {services.map((service, index) => (
                        <div key={index} className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow text-center">
                            <div className="flex justify-center mb-4">
                                {service.icon}
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-3">
                                {service.title}
                            </h3>
                            <p className="text-gray-600">
                                {service.description}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Emergency Steps */}
                <div className="bg-orange-50 rounded-lg p-8 mb-12">
                    <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">
                        Acil Durumda Ne Yapmalı?
                    </h2>
                    <div className="grid md:grid-cols-4 gap-6">
                        {emergencySteps.map((step) => (
                            <div key={step.step} className="text-center">
                                <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-lg">
                                    <span className="text-4xl">{step.icon}</span>
                                </div>
                                <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-3 text-sm font-bold">
                                    {step.step}
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 mb-2">
                                    {step.title}
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Coverage Area */}
                <div className="grid md:grid-cols-2 gap-8 mb-12">
                    <div className="bg-white p-8 rounded-lg shadow-lg">
                        <div className="flex items-center mb-4">
                            <EnvironmentOutlined className="text-3xl text-orange-600 mr-3" />
                            <h3 className="text-2xl font-bold text-gray-800">Hizmet Bölgemiz</h3>
                        </div>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-center">
                                <span className="text-orange-600 mr-2">✓</span>
                                Sakarya Merkez ve Tüm İlçeler
                            </li>
                            <li className="flex items-center">
                                <span className="text-orange-600 mr-2">✓</span>
                                TEM ve D-100 Otoyolu
                            </li>
                            <li className="flex items-center">
                                <span className="text-orange-600 mr-2">✓</span>
                                Ankara - İstanbul Arası
                            </li>
                            <li className="flex items-center">
                                <span className="text-orange-600 mr-2">✓</span>
                                Kocaeli, Düzce, Bilecik Çevresi
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white p-8 rounded-lg shadow-lg">
                        <div className="flex items-center mb-4">
                            <ClockCircleOutlined className="text-3xl text-orange-600 mr-3" />
                            <h3 className="text-2xl font-bold text-gray-800">Çalışma Saatlerimiz</h3>
                        </div>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center border-b pb-2">
                                <span className="font-semibold text-gray-700">Yol Yardım</span>
                                <span className="text-green-600 font-bold">7/24 Açık</span>
                            </div>
                            <div className="flex justify-between items-center border-b pb-2">
                                <span className="font-semibold text-gray-700">Mağaza</span>
                                <span className="text-gray-700">08:00 - 19:00</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700">Pazar Günü</span>
                                <span className="text-gray-700">09:00 - 17:00</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg p-12 text-center">
                    <h2 className="text-3xl font-bold mb-4">Acil Durumda Hemen Arayın!</h2>
                    <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto">
                        Mobil ekibimiz 30 dakika içinde yanınızda olacak. Yolda kalmayın, biz size gelelim!
                    </p>
                    <Button
                        size="large"
                        className="bg-red-600 hover:bg-red-700 text-white border-none h-14 px-12 text-lg"
                    >
                        <a href="tel:+905441205454" className="flex items-center text-white">
                            <PhoneOutlined className="mr-2 text-xl" />
                            0544 120 54 54
                        </a>
                    </Button>
                    <p className="text-sm text-gray-400 mt-4">
                        * Yol yardım hizmeti ücretlidir. Fiyat bilgisi için arayınız.
                    </p>
                </div>
            </div>
        </MainLayout>
    );
};

export default Help;
